/**
 * useFilterState.ts
 * Single hook that owns filter state, sort mode, and URL sync.
 */

import { useState, useEffect, useCallback } from 'react'
import type { FilterState, SortMode, DayOfWeek, DealType, VenueCategory, PriceTier, TimeWindow } from '../types'
import { DEFAULT_FILTERS } from '../types'
import { filtersToParams, paramsToFilters, countActiveFilters, isDefaultState } from '../utils/filterUrl'

export interface UseFilterStateReturn {
  filters: FilterState
  sort: SortMode
  activeCount: number
  isDefault: boolean

  setFilter: <K extends keyof FilterState>(key: K, value: FilterState[K]) => void
  toggleDay: (day: DayOfWeek) => void
  toggleDealType: (t: DealType) => void
  toggleCategory: (c: VenueCategory) => void
  setCategories:  (cats: VenueCategory[]) => void
  setDealTypes:   (types: DealType[]) => void
  toggleNeighborhood: (n: string) => void
  togglePriceTier: (p: PriceTier) => void
  setTimeWindow: (tw: TimeWindow | null) => void
  setOpenNow: (on: boolean) => void
  setDogFriendly: (df: boolean) => void
  setSearch: (q: string) => void
  setSort: (mode: SortMode) => void
  clearAll: () => void
}

const DEFAULT_SORT: SortMode = 'featured'

function defaultFiltersWithToday(): FilterState {
  return { ...DEFAULT_FILTERS }
}

export function useFilterState(): UseFilterStateReturn {
  const [filters, setFilters] = useState<FilterState>(() => {
    try {
      const params = new URLSearchParams(window.location.search)
      if (params.toString() === '') return defaultFiltersWithToday()
      const { filters: parsed } = paramsToFilters(params)
      return parsed
    } catch {
      return defaultFiltersWithToday()
    }
  })

  const [sort, setSort] = useState<SortMode>(() => {
    try {
      const params = new URLSearchParams(window.location.search)
      if (params.toString() === '') return DEFAULT_SORT
      const { sort: parsed } = paramsToFilters(params)
      return parsed
    } catch {
      return DEFAULT_SORT
    }
  })

  useEffect(() => {
    try {
      const params = filtersToParams(filters, sort)
      const newSearch = params.toString()
      const currentSearch = window.location.search.replace(/^\?/, '')
      if (newSearch !== currentSearch) {
        window.history.replaceState(null, '', newSearch ? `?${newSearch}` : window.location.pathname)
      }
    } catch {
      // silently ignore — URL sync is best-effort
    }
  }, [filters, sort])

  const setFilter = useCallback(<K extends keyof FilterState>(
    key: K,
    value: FilterState[K]
  ) => {
    setFilters(f => ({ ...f, [key]: value }))
  }, [])

  function toggleInSet<T>(key: keyof FilterState, val: T) {
    setFilters(f => {
      const current = f[key] as Set<T>
      const next = new Set(current)
      next.has(val) ? next.delete(val) : next.add(val)
      return { ...f, [key]: next }
    })
  }

  const clearAll = useCallback(() => {
    setFilters(defaultFiltersWithToday())
    setSort(DEFAULT_SORT)
  }, [])

  return {
    filters,
    sort,
    activeCount: countActiveFilters(filters, sort),
    isDefault: isDefaultState(filters, sort),

    setFilter,
    toggleDay:          (d) => toggleInSet<DayOfWeek>('days', d),
    toggleDealType:     (t) => toggleInSet<DealType>('dealTypes', t),
    toggleCategory:     (c) => toggleInSet<VenueCategory>('categories', c),
    setCategories:      (cats: VenueCategory[]) => setFilter('categories', new Set(cats) as any),
    setDealTypes:       (types: DealType[]) => setFilter('dealTypes', new Set(types) as any),
    toggleNeighborhood: (n) => toggleInSet<string>('neighborhoods', n),
    togglePriceTier:    (p) => toggleInSet<PriceTier>('priceTiers', p),
    setTimeWindow:      (tw) => setFilter('timeWindow', tw),
    setOpenNow:         (on) => setFilter('openNow', on),
    setDogFriendly:     (df) => setFilter('dogFriendly', df),  // ← no more as any
    setSearch:          (q)  => setFilter('search', q),
    setSort,
    clearAll,
  }
}
